import { pick } from 'lodash'

import { useGenerateReport } from '~/app/accounting/useGenerateReport'

/**
 * Table exports: one CSV per tab, scoped to exactly what that tab is showing.
 *
 * Anything beyond the visible rows — a statement, a PDF, the lot timeline —
 * is a report, and lives in the Reports tab.
 */

// Mirrors the Transactions tab, which reads every filter in the bar.
export const useTransactionsExport = (filterParams: Record<string, string>) =>
  useGenerateReport({ kind: 'transactions', filterParams })

// The Tokens tab is scoped by account, network and period; its search box
// filters client-side, so it rides along as its own param.
export const useTokensExport = ({
  filterParams,
  search,
}: {
  filterParams: Record<string, string>
  search?: string
}) => {
  const tokensFilterParams = pick(filterParams, [
    'accountIds',
    'chainIds',
    'startDate',
    'endDate',
  ])
  const trimmedSearch = search?.trim()
  return useGenerateReport({
    kind: 'tokens',
    filterParams: trimmedSearch
      ? { ...tokensFilterParams, search: trimmedSearch }
      : tokensFilterParams,
  })
}

// Open lots are a position as of an instant, so the tab's period end is the
// cutoff and its start is meaningless. An omitted cutoff means "as of now" —
// the server defaults it.
export const useOpenLotsExport = (filterParams: Record<string, string>) => {
  const lotFilterParams = pick(filterParams, [
    'accountIds',
    'chainIds',
    'tokens',
  ])
  const openAsOf = filterParams.endDate
  return useGenerateReport({
    kind: 'lots',
    filterParams: openAsOf ? { ...lotFilterParams, openAsOf } : lotFilterParams,
  })
}
